// Bouwt het `d`-object voor buildBoorprofielSvg() uit een platte boring (API-vorm).
// Gedeeld door de boring-detail-tab en de Export-voorvertoning. NEN 6693 slaat NEN-codes
// op; die worden hier naar volledige termen geresolved (NEN eerst, TI-terugval voor 5104).
import {
  BoringModel, nen6693, tiCodelists, formatHeight, mediaanApplies, bzbFillsLayer,
  REFERENCE_PLANES, HEIGHT_DATUMS, DEFAULT_HEIGHT_DATUM,
} from '@mv/models';
import { getCompanyTextures } from './company-textures.js';

const codeToName = (code, map) => {
  if (!code) return '';
  for (const [name, id] of map) if (id === code) return name;
  return code;
};
/** Gesteentecodes — zie isRock in de laagopbouw. */
const ROCK_CODES = new Set(nen6693.GESTEENTE.values());

const nenName = (code, nenMaps, tiMap) => {
  if (!code) return '';
  for (const m of nenMaps) for (const [name, id] of m) if (id === code) return name;
  return tiMap ? codeToName(code, tiMap) : '';
};

/**
 * De BZB's van een laag zoals de tekening ze nodig heeft: code, naam en of de laag eruit
 * bestaat (dan vult de BZB het hele textuurvak). NEN 6693 bewaart de NEN-afkorting, NEN 5104
 * de SIKB-code; de naam komt uit de lijst die bij de standaard hoort.
 *
 * @param {{bzb?: Array<{code?:string|null,type?:string|null,gradation?:string|null}>}} layerModel
 * @param {string|null} standard
 */
export function profileBzb(layerModel, standard) {
  const nenMaps = standard === 'nen5104' ? [] : [nen6693.BZB_SOORT, nen6693.GESTEENTE];
  return (layerModel?.bzb || [])
    .filter((b) => b.code || b.type)
    .map((b) => ({
      code: b.code || null,
      label: (b.code && nenName(b.code, nenMaps, tiCodelists.LaSpecialCompounds)) || b.type || b.code,
      fillsLayer: bzbFillsLayer(b.gradation),
    }));
}

/**
 * @param {object} rawBoring  Platte boring incl. layers/peilbuizen/afwerking (API-vorm).
 * @param {{ heightDatum?: string, standard?: string, textures?: object }} [opts]  textures: afwijkingen
 *   van het bedrijf; standaard die van het actieve bedrijf (utils/company-textures.js).
 * @returns {object|null}  `d` voor buildBoorprofielSvg, of null als er geen lagen zijn.
 */
export function buildBoorprofielData(rawBoring, opts = {}) {
  if (!rawBoring) return null;
  const bm = BoringModel.fromFlat(rawBoring);
  const layerModels = bm.layers || [];
  if (!layerModels.length) return null;

  const datum = HEIGHT_DATUMS[opts.heightDatum] ? opts.heightDatum : DEFAULT_HEIGHT_DATUM;
  const standard = rawBoring._standard || opts.standard || null;
  const rp = rawBoring.referencePlane ? REFERENCE_PLANES[rawBoring.referencePlane]?.label : null;

  const layers = layerModels.map(l => {
    const admix = [[l.toevoeging1, l.gradatie1], [l.toevoeging2, l.gradatie2], [l.toevoeging3, l.gradatie3]]
      .filter(([t]) => t)
      .map(([t, g]) => ({
        label: nenName(t, [nen6693.BIJMENGING], tiCodelists.toevoeging),
        grad:  g ? nenName(g, [nen6693.INTENSITEIT], tiCodelists.gradatie) : '',
      }));
    // 'S' is in NEN 6693 SILT en in de SIKB-lijst SLIB. Bij een 5104-boring mag de
    // NEN 6693-lijst er dus niet overheen: dan wordt slib silt, met het verkeerde patroon
    // in de tekening. De standaard komt van de boring (`_standard`, zoals de export 'm ook
    // per boring bepaalt); is die onbekend, dan blijft NEN 6693 leidend zoals hiervoor.
    const soilLabel = standard === 'nen5104'
      ? (l.soilLabel || '')
      : (nenName(l.soilType, [nen6693.GRONDSOORT, nen6693.LAAGTYPE_SPECIAAL, nen6693.GESTEENTE], tiCodelists.bodemsoortHistorisch) || l.soilLabel || '');
    // Zelfde verhaal voor de kleur: l.kleurLabel komt uit de SIKB-tabellen, en daar heet de
    // tint 'DR', niet 'do'. Een NEN 6693-laag kreeg zo "do bruin" in de pdf. Tint, hoofd- en
    // bijkleur hier uit de NEN 6693-lijsten; lukt dat niet, dan het label van het model.
    // Alleen als ELKE aanwezige code in die lijsten staat; een SIKB-code ('DR', 'BR') bij een
    // boring zonder bekende standaard geeft anders een half label.
    const delen = [[l.kleurShade, nen6693.KLEUR_TINT], [l.kleurMain, nen6693.KLEUR], [l.kleurSub, nen6693.KLEUR]]
      .filter(([c]) => c).map(([c, m]) => nenName(c, [m]));
    const kleur6693 = delen.length && delen.every(Boolean) ? delen.join(' ') : '';
    const kleurLabel = standard === 'nen5104' ? (l.kleurLabel || '') : (kleur6693 || l.kleurLabel || '');
    const line2 = [
      (mediaanApplies(l.soilType) && l.mediaanLabel) ? `mediaan ${l.mediaanLabel}` : '',
      kleurLabel,
      (l.bzbLabels || []).join(', '),
      l.opmerking || '',
    ].filter(Boolean).join(' · ');
    // Een gesteente is aan zijn naam niet te herkennen ('zandsteen' bevat 'zand'), dus
    // zeggen we het er hier bij — hier weten we nog uit welke lijst de code komt.
    const isRock = !!l.soilType && ROCK_CODES.has(l.soilType);
    const bzb = profileBzb(l, standard);
    // Geen grondsoort, wel een BZB (een asfaltlaag zonder hoeveelheid, een puinlaag): dan bestaat
    // de laag uit die BZB. Zo heet hij 'Asfalt' en krijgt hij de asfalttextuur, in plaats van
    // 'grondsoort onbekend' met een leeg vak. Dezelfde regel als het lagenoverzicht.
    if (!l.soilType && bzb.length && !bzb.some((b) => b.fillsLayer)) bzb[0] = { ...bzb[0], fillsLayer: true };
    return {
      from: Number(l.upperDepth) || 0, to: Number(l.lowerDepth) || 0, soilLabel, admix, line2, isRock,
      soilCode: l.soilType || null, bzb,
    };
  });

  const afw = rawBoring.afwerking || {};
  const peil = rawBoring.peilbuizen || [];
  return {
    header: {
      name: bm.name || rawBoring.name || '',
      maaiveld: rawBoring.z != null ? formatHeight(rawBoring.z, datum) : null,
      referencePlane: rp,
      boormeester: bm.boormeester || rawBoring.boormeester || null,
    },
    layers,
    fillings: (afw.fillings || []).map(f => ({ van: f.van, tot: f.tot, code: f.materiaal })),
    bovenafwerking: (() => {
      const top = (afw.topCompletions || [])[0];
      const type = top ? nen6693.bovenafwerkingMigrate(top.type, top.materiaal) : null;
      return type ? { type, label: nen6693.bovenafwerkingLabel(type) || type } : null;
    })(),
    achtergelaten: (afw.achtergelatenObjecten || []).map(o => ({ van: o.van, tot: o.tot, aard: o.aard, type: o.type || null, typeLabel: nen6693.achtergelatenTypeLabel(o.type) })),
    peilbuizen: peil.map(pb => ({ filterTop: pb.filterTopCm ?? pb.filter_from_cm ?? null, filterBottom: pb.filterBottomCm ?? pb.filter_to_cm ?? null })),
    gwsCmMv: rawBoring.gwsCmMv ?? null,
    boordiepteCm: Math.max(0, ...layerModels.map(l => Number(l.lowerDepth) || 0)),
    standard,
    textures: opts.textures ?? getCompanyTextures(),
  };
}
